<template>
  <div
    class="w-full px-4 py-2 flex tablet-h:rounded-[10px] tablet-h:p-1.5 tablet-h:pr-2.5"
    :class="classes"
    @click="handleRowClick"
  >
    <div class="relative mr-[9px] size-11 shrink-0 p-0.5">
      <div
        v-if="copyright?.file?.type"
        class="size-full rounded-[4px] bg-gray-200/30 p-0.5"
      >
        <Thumbnail
          :type="fileTypeToThumbnail(copyright.file.type)"
          :src="
            applyAuthorizationToken(notification.primaryIcon.imageUrl) ??
            undefined
          "
        />
      </div>
      <Avatar
        v-else
        :size="40"
        :src="notification.primaryIcon.imageUrl ?? undefined"
      />
      <Avatar
        v-if="notification.secondaryIcon?.imageUrl"
        class="absolute bottom-0 right-0 z-2"
        :size="18"
        :src="notification.secondaryIcon.imageUrl"
      />
    </div>

    <div
      class="text-[14px] leading-[19px] text-gray-800 -mt-1 flex flex-col justify-between"
    >
      <component :is="copy" class="mb-[3px]" />
      <div class="flex items-center justify-between">
        <div class="text-[12px] leading-[18px] opacity-60">
          {{ formatDistanceToNowStrict(props.notification.timestamp) }} ago
        </div>

        <Pill v-if="showStatus" :variant="statusVariant">
          {{ status }}
        </Pill>
      </div>
      <div v-if="showActionButtons" class="w-full flex gap-x-2.5 mt-2">
        <Button
          class="flex-1"
          size="md"
          variant="primary"
          :loading="loadingAccept"
          :disabled="loadingDecline"
          @click.stop="handleAcceptClick"
        >
          Accept
        </Button>
        <Button
          class="flex-1"
          size="md"
          variant="stroke"
          :loading="loadingDecline"
          :disabled="loadingAccept"
          @click.stop="handleDeclineClick"
        >
          Decline
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  InviteStatus,
  NotificationType,
  useAcceptInviteMutation,
  useDeclineInviteMutation,
  type Copyright,
  type InviteMetadata,
  type Notification,
} from '@/shared/graphql/legacy/generated'
import { apolloClient } from '@/shared/graphql/client'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import {
  Avatar,
  Button,
  Pill,
  Thumbnail,
  type PillVariant,
} from '@dacr/library'
import { formatDistanceToNowStrict } from 'date-fns'
import { computed, h } from 'vue'
import { useRouter } from 'vue-router'

const props = defineProps<{
  notification: Notification
}>()

const router = useRouter()

const { loading: loadingAccept, mutate: acceptInvite } =
  useAcceptInviteMutation()

const { loading: loadingDecline, mutate: declineInvite } =
  useDeclineInviteMutation()

const metadata = computed(() => props.notification.metadata as InviteMetadata)

const copyright = computed(() => metadata.value.invite.reference as Copyright)

const isFileInvite = computed(
  () =>
    metadata.value.invite.reference.__typename === 'Dacr' &&
    !!copyright.value?.id
)

const classes = computed(() => ({
  'tablet-h:hover:bg-[#FAFAFA]': props.notification.read,
  'bg-green-700/10': !props.notification.read,
  'cursor-pointer': isFileInvite.value,
}))

const handleRowClick = () => {
  if (!isFileInvite.value) return

  router.push(`/file/${copyright.value.id}`)
}

const showStatus = computed(
  () =>
    !!(isRequest.value && metadata.value.invite.status !== InviteStatus.Pending)
)

const isAccepted = computed(
  () => metadata.value.invite.status === InviteStatus.Accepted
)

const isRemoved = computed(
  () => metadata.value.invite.status === InviteStatus.Removed
)

const statusVariant = computed<PillVariant>(() => {
  if (isAccepted.value) return 'success'
  if (isRemoved.value) return 'danger'
  return 'default'
})

const status = computed(() => {
  if (isRemoved.value) {
    return 'Request removed'
  }

  return `${metadata.value.invite.status.charAt(0)}${metadata.value.invite.status.slice(1).toLowerCase()}`
})

const isRequest = computed(
  () =>
    props.notification.type === NotificationType.TeamMemberAccessPending ||
    props.notification.type === NotificationType.FileOwnerAccessPending ||
    props.notification.type === NotificationType.FileAuthorAccessPending
)

const showActionButtons = computed(
  () => isRequest.value && metadata.value.invite.status === InviteStatus.Pending
)

const copy = computed(() => {
  if (props.notification.type === NotificationType.FileOwnerAccessAccepted) {
    return h('div', [
      h('span', { class: 'font-medium' }, metadata.value.invite?.to?.name),
      ' has accepted your request to be listed as an owner for the copyright of ',
      h(
        'span',
        { class: 'font-medium break-all' },
        copyright.value.file?.fileName
      ),
      '.',
    ])
  }

  if (props.notification.type === NotificationType.FileAuthorAccessAccepted) {
    return h('div', [
      h('span', { class: 'font-medium' }, metadata.value.invite?.to?.name),
      ' has accepted your request to be listed as an author for the copyright of ',
      h(
        'span',
        { class: 'font-medium break-all' },
        copyright.value.file?.fileName
      ),
      '.',
    ])
  }

  if (props.notification.type === NotificationType.FileOwnerAccessPending) {
    return h('div', [
      h('span', { class: 'font-medium' }, metadata.value.invite?.from?.name),
      ' has requested to list you as one of the owners for the copright of ',
      h(
        'span',
        { class: 'font-medium break-all' },
        copyright.value.file?.fileName
      ),
      ' Accept the request to be listed as an owner.',
    ])
  }

  if (props.notification.type === NotificationType.FileAuthorAccessPending) {
    return h('div', [
      h('span', { class: 'font-medium' }, metadata.value.invite?.from?.name),
      ' has requested to list you as one of the authors for the copright of ',
      h(
        'span',
        { class: 'font-medium break-all' },
        copyright.value.file?.fileName
      ),
      ' Accept the request to be listed as an author.',
    ])
  }

  if (props.notification.type === NotificationType.FilePrivacyAccessAccepted) {
    return h('div', [
      h('span', { class: 'font-medium' }, metadata.value.invite?.from?.name),
      ' has invited you to view ',
      h(
        'span',
        { class: 'font-medium break-all' },
        copyright.value.file?.fileName
      ),
      `.`,
    ])
  }

  if (
    props.notification.type === NotificationType.TeamMemberAccessPending &&
    metadata.value.invite.reference.__typename === 'Origin'
  ) {
    return h('div', [
      `You have been invited to join the `,

      h('span', { class: 'font-medium' }, metadata.value.invite.reference.name),
      ` team. Accept the invite to join the team.`,
    ])
  }

  if (
    props.notification.type === NotificationType.TeamMemberAccessAccepted &&
    metadata.value.invite.reference.__typename === 'Origin'
  ) {
    return h('div', [
      h('span', { class: 'font-medium' }, metadata.value.invite.to.name),
      ' has accepted your invite to join the ',
      h('span', { class: 'font-medium' }, metadata.value.invite.reference.name),
      ` team.`,
    ])
  }

  return null
})

const updateNotificationStatus = (status: InviteStatus) => {
  apolloClient.cache.modify({
    id: `Invite:${(props.notification.metadata as InviteMetadata).invite.id}`,
    fields: {
      status() {
        return status
      },
    },
  })
}

const handleAcceptClick = async () => {
  try {
    const metadata = props.notification.metadata as InviteMetadata
    await acceptInvite({
      id: metadata.invite.id,
    })
    updateNotificationStatus(InviteStatus.Accepted)
  } catch (error) {
    console.error('Action failed', error)
  }
}

const handleDeclineClick = async () => {
  try {
    const metadata = props.notification.metadata as InviteMetadata
    await declineInvite({
      id: metadata.invite.id,
    })
    updateNotificationStatus(InviteStatus.Declined)
  } catch (error) {
    console.error('Action failed', error)
  }
}
</script>
